import React, { useState, useEffect } from 'react';
import { apprenantsAPI, configAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import ModalDetails from '../components/ModalDetails';
import ModalEdit from '../components/ModalEdit';
import ModalChangementEntreprise from '../components/ModalChangementEntreprise';
import Icon from '../components/Icon';

const Liste = () => {
  const { isAdmin, isFormateur } = useAuth();
  const navigate = useNavigate();
  const [apprenants, setApprenants] = useState([]);
  const [filteredApprenants, setFilteredApprenants] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortCol, setSortCol] = useState('nom');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 15;
  const [selectedApprenant, setSelectedApprenant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChangementModal, setShowChangementModal] = useState(false);

  useEffect(() => {
    fetchData();
    fetchConfig();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [apprenants, search, filterType, sortCol, sortDir]);

  const fetchData = async () => {
    try {
      const response = await apprenantsAPI.getAll({ limit: 1000 });
      setApprenants(response.data.apprenants || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des apprenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await configAPI.get();
      setConfig(response.data);
    } catch (error) {
      console.error('Erreur config');
    }
  };

  const filterAndSort = () => {
    let filtered = [...apprenants];

    // Recherche
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(a =>
        a.nom?.toLowerCase().includes(searchLower) ||
        a.filiere?.toLowerCase().includes(searchLower) ||
        a.entreprise?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre type
    if (filterType) {
      filtered = filtered.filter(a => a.typeFormation === filterType);
    }

    // Tri
    filtered.sort((a, b) => {
      const aVal = a[sortCol] || '';
      const bVal = b[sortCol] || '';
      if (sortDir === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      } else {
        return bVal.toString().localeCompare(aVal.toString());
      }
    });

    setFilteredApprenants(filtered);
    setCurrentPage(1);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet apprenant ?')) return;
    if (!isAdmin) {
      toast.error('Pas de droits');
      return;
    }

    try {
      await apprenantsAPI.delete(id);
      toast.success('Apprenant supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const exportExcel = () => {
    const data = filteredApprenants.map(a => ({
      Nom: a.nom,
      Prenom: a.prenom,
      Genre: a.genre,
      Âge: a.age,
      Téléphone: a.telephone,
      Email: a.email,
      Diplôme: a.diplome,
      'Statut Entrée': a.statutEntree,
      'Type Formation': a.typeFormation,
      Filière: a.filiere,
      'Date Début': a.dateDebut ? format(new Date(a.dateDebut), 'dd/MM/yyyy') : '',
      'Date Fin': a.dateFin ? format(new Date(a.dateFin), 'dd/MM/yyyy') : '',
      Promotion: a.promotion,
      'Statut Insertion': a.statutInsertion,
      'Date Insertion': a.dateInsertion ? format(new Date(a.dateInsertion), 'dd/MM/yyyy') : '',
      'Type Contrat': a.typeContrat,
      Entreprise: a.entreprise,
      Poste: a.poste,
      Salaire: a.salaire || a.salaireTranche || ''
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Apprenants');
    XLSX.writeFile(wb, `SIMPLON_CI_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel exporté !');
  };

  const isAcculturation = (type) => type?.toLowerCase().includes('acculturation');
  const isSortiePositive = (statut) => config?.statutsInsertion?.positif?.includes(statut);

  const getSuiviStatus = (apprenant) => {
    // Logique simplifiée - à améliorer
    if (!apprenant.dateInsertion) return 'none';
    return 'ok';
  };

  const getSuiviBadge = (status) => {
    const badges = {
      ok: '🟢',
      attente: '🟡',
      retard: '🔴',
      none: '⚪'
    };
    return badges[status] || '⚪';
  };

  const totalPages = Math.ceil(filteredApprenants.length / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const paginatedData = filteredApprenants.slice(startIndex, startIndex + perPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-card bg-white rounded-xl p-6">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <h2 className="text-xl font-bold flex items-center text-gray-800">
            <Icon name="liste" size={24} className="mr-2 text-red-600" />
            Apprenants
          </h2>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Icon name="search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                id="liste-search"
                name="search"
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-48 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              />
            </div>
            <select
              id="liste-filter-type"
              name="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              autoComplete="off"
            >
              <option value="">Tous types</option>
              {config?.typesFormation?.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Icon name="download" size={18} />
              <span>Excel</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-3 py-3 text-left font-semibold cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nom')}
                >
                  Nom {sortCol === 'nom' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                  <th
  className="px-3 py-3 text-left font-semibold cursor-pointer hover:bg-gray-100"
  onClick={() => handleSort('prenom')}
>
  Prenom {sortCol === 'prenom' && (sortDir === 'asc' ? '↑' : '↓')}
</th>
                <th className="px-3 py-3 text-center font-semibold">Genre</th>
                <th className="px-3 py-3 text-center font-semibold">Âge</th>
                <th className="px-3 py-3 text-left font-semibold">Référentiel</th>
                <th className="px-3 py-3 text-left font-semibold">Type</th>
                <th className="px-3 py-3 text-center font-semibold">Statut</th>
                <th className="px-3 py-3 text-center font-semibold">Suivi</th>
                <th className="px-3 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedData.map((apprenant) => {
                const suiviStatus = getSuiviStatus(apprenant);
                const badge = getSuiviBadge(suiviStatus);
                const canEdit = isFormateur;
                const canDelete = isAdmin;
                const isPositive = isSortiePositive(apprenant.statutInsertion);

                return (
                  <tr key={apprenant._id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 font-medium">{apprenant.nom}</td>
                    <td className="px-3 py-3 font-medium"> {apprenant.prenom || '-'}</td>
                    <td className="px-3 py-3 text-center">
                      {apprenant.genre === 'Femme' ? '👩' : '👨'}
                    </td>
                    <td className="px-3 py-3 text-center text-sm">{apprenant.age || '-'}</td>
                    <td className="px-3 py-3 text-sm">{apprenant.filiere || '-'}</td>
                    <td className="px-3 py-3 text-sm">
                      {isAcculturation(apprenant.typeFormation) ? '🎓' : '💼'}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          apprenant.statutInsertion === 'N/A'
                            ? 'bg-gray-100'
                            : isPositive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {apprenant.statutInsertion || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center text-lg">{badge}</td>
                    <td className="px-3 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedApprenant(apprenant);
                            setShowDetailsModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Voir"
                          aria-label="Voir les détails"
                        >
                          <Icon name="view" size={18} />
                        </button>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedApprenant(apprenant);
                                setShowEditModal(true);
                              }}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Modifier"
                              aria-label="Modifier"
                            >
                              <Icon name="edit" size={18} />
                            </button>
                            {!isAcculturation(apprenant.typeFormation) && isPositive && (
                              <button
                                onClick={() => {
                                  setSelectedApprenant(apprenant);
                                  setShowChangementModal(true);
                                }}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title="Changement entreprise"
                                aria-label="Changement entreprise"
                              >
                                <Icon name="refresh" size={18} />
                              </button>
                            )}
                          </>
                        )}
                        {!isAcculturation(apprenant.typeFormation) && canEdit && (
                          <button
                            onClick={() => navigate(`/suivi?apprenant=${apprenant._id}`)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Suivi"
                          >
                            <Icon name="phone" size={18} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(apprenant._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Icon name="delete" size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Aucun apprenant trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {filteredApprenants.length} apprenant{filteredApprenants.length > 1 ? 's' : ''}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              ◀
            </button>
            <span className="px-3 py-1 bg-gray-100 rounded">
              {currentPage}/{totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              ▶
            </button>
          </div>
        </div>
      </div>

      {/* Modales */}
      <ModalDetails
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedApprenant(null);
        }}
        apprenant={selectedApprenant}
      />

      <ModalEdit
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedApprenant(null);
        }}
        apprenant={selectedApprenant}
        onUpdate={() => {
          fetchData();
          setShowEditModal(false);
          setSelectedApprenant(null);
        }}
      />

      <ModalChangementEntreprise
        isOpen={showChangementModal}
        onClose={() => {
          setShowChangementModal(false);
          setSelectedApprenant(null);
        }}
        apprenant={selectedApprenant}
        onUpdate={() => {
          fetchData();
          setShowChangementModal(false);
          setSelectedApprenant(null);
        }}
      />
    </div>
  );
};

export default Liste;
